import React, { useState } from 'react';
import { MainContentPanel } from '../../components/MainContentPanel';
import { Typography, IconButton, Menu, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import { SearchableLicenseTable } from './SearchableLicenseTable';
import { Add } from '@mui/icons-material';
import { GET_LICENSES, GET_LICENSES_EXPORT } from './License.graphql';
import LoadingSpinner from '../../components/LoadingSpinner';
import SearchMaconomyCustomerDialog from '../details/SearchMaconomyCustomerDialog';
import AddLicenseDialog from './AddLicenseDialog';
import { MCustomer } from '../../interfaces';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { initialSettings, ISettings, LicenseOverviewModel } from './ColumnDefinitions';
import { useLocation } from 'react-router-dom';
import moment from 'moment';

interface LicenseOverviewQueryResult {
  licenses?: {
    licenses?: LicenseOverviewModel[];
    count?: number;
    page?: number;
    pageSize?: number;
  };
}

interface LicenseExportQueryResult {
  licenses?: LicenseOverviewModel[];
}

export function LicenseOverview(props) {
  void props;

  let searchParms = new URLSearchParams(useLocation().search);
  let pendingTermination = searchParms.get('filter') === 'pendingTermination';
  let locked = searchParms.get('filter') === 'locked';
  let adfs = searchParms.get('filter') === 'adfs';

  const getDefaultDates = () => {
    const today = moment();
    const oneYearAgo = moment().subtract(1, 'year');
    return { fromDate: oneYearAgo, toDate: today };
  };

  const defaultDates = getDefaultDates();

  // Helper functions for sessionStorage
  const getStoredBoolean = (key: string, defaultValue: boolean) => {
    const stored = sessionStorage.getItem(key);
    return stored !== null ? stored === 'true' : defaultValue;
  };

  const getStoredNumber = (key: string, defaultValue: number) => {
    const stored = sessionStorage.getItem(key);
    return stored !== null ? parseInt(stored, 10) : defaultValue;
  };

  const getStoredDate = (key: string, defaultValue: moment.Moment | null) => {
    const stored = sessionStorage.getItem(key);
    return stored !== null ? moment(stored) : defaultValue;
  };

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [openSearch, setOpenSearch] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTermParent, setSearchTermParent] = useState(sessionStorage.getItem('licenseSearchTerm') ?? '');
  const [customer, setCustomer] = useState<MCustomer>();
  const [series, setSeries] = useState(getStoredNumber('licenseFilterSeries', 0));
  const [onlyActive, setOnlyActive] = useState(getStoredBoolean('licenseFilterOnlyActive', pendingTermination || locked || adfs ? false : false));
  const [onlyLocked, setOnlyLocked] = useState(getStoredBoolean('licenseFilterOnlyLocked', locked));
  const [onlyPendTerm, setOnlyPendTerm] = useState(getStoredBoolean('licenseFilterOnlyPendTerm', pendingTermination));
  const [onlyADFS, setOnlyADFS] = useState(getStoredBoolean('licenseFilterOnlyADFS', adfs));
  const [columnlist, setColumnList] = useState<ISettings[]>(initialSettings);
  const [onlyInformed, setOnlyInformed] = useState(getStoredBoolean('licenseFilterOnlyInformed', false));
  const [enableDateFilter, setEnableDateFilter] = useState(getStoredBoolean('licenseFilterEnableDateFilter', false));
  const [fromDate, setFromDate] = useState<moment.Moment | null>(getStoredDate('licenseFilterFromDate', defaultDates.fromDate));
  const [toDate, setToDate] = useState<moment.Moment | null>(getStoredDate('licenseFilterToDate', defaultDates.toDate));
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });

  const resetToFirstPage = () => setPaginationModel((current) => ({ ...current, page: 0 }));
  const selectedColumns = columnlist.filter((c) => c.showColumn).map((c) => c.columnName);
  const licenseFilterVariables = {
    searchTerm: searchTermParent || undefined,
    series,
    onlyActive,
    onlyLocked,
    onlyPendTerm,
    onlyADFS,
    onlyInformed,
    getColumn: selectedColumns,
    enableDateFilter,
    fromDate: fromDate?.toISOString(),
    toDate: toDate?.toISOString(),
  };

  const { loading, error, data } = useQuery<LicenseOverviewQueryResult>(GET_LICENSES, {
    variables: {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      ...licenseFilterVariables,
    },
    fetchPolicy: 'no-cache',
  });
  const [getExportLicenses, { loading: exportLoading }] = useLazyQuery<LicenseExportQueryResult>(GET_LICENSES_EXPORT, {
    fetchPolicy: 'no-cache',
  });

  const licenses = data?.licenses?.licenses ?? [];
  const rowCount = data?.licenses?.count ?? 0;

  const loadExportLicenses = async () => {
    const result = await getExportLicenses({
      variables: licenseFilterVariables,
    });

    return result.data?.licenses ?? [];
  };

  const handleMenuClick = (setState: (boolean) => void) => {
    setMenuAnchor(null);
    setState(true);
  };

  const handleCustomer = (customer: MCustomer) => {
    setOpenSearch(false);
    setCustomer(customer);
  };

  const handler = (ids: boolean[]) => {
    const newArr = columnlist.map((column) => ({ ...column }));
    newArr.forEach((c, index) => {
      c.showColumn = ids[index];
    });

    setColumnList(newArr);
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setSeries(newValue);
    sessionStorage.setItem('licenseFilterSeries', newValue.toString());
    resetToFirstPage();
  };

  const handleSetSearchTermParent = (val: string) => {
    setSearchTermParent(val);
    sessionStorage.setItem('licenseSearchTerm', val);
    resetToFirstPage();
  };

  // Wrapper functions to save to sessionStorage
  const handleSetOnlyActive = (val: boolean) => {
    setOnlyActive(val);
    sessionStorage.setItem('licenseFilterOnlyActive', val.toString());
    resetToFirstPage();
  };

  const handleSetOnlyLocked = (val: boolean) => {
    setOnlyLocked(val);
    sessionStorage.setItem('licenseFilterOnlyLocked', val.toString());
    resetToFirstPage();
  };

  const handleSetOnlyPendTerm = (val: boolean) => {
    setOnlyPendTerm(val);
    sessionStorage.setItem('licenseFilterOnlyPendTerm', val.toString());
    resetToFirstPage();
  };

  const handleSetOnlyADFS = (val: boolean) => {
    setOnlyADFS(val);
    sessionStorage.setItem('licenseFilterOnlyADFS', val.toString());
    resetToFirstPage();
  };

  const handleSetOnlyInformed = (val: boolean) => {
    setOnlyInformed(val);
    sessionStorage.setItem('licenseFilterOnlyInformed', val.toString());
    resetToFirstPage();
  };

  const handleSetEnableDateFilter = (val: boolean) => {
    setEnableDateFilter(val);
    sessionStorage.setItem('licenseFilterEnableDateFilter', val.toString());
    resetToFirstPage();
  };

  const handleSetFromDate = (val: moment.Moment | null) => {
    setFromDate(val);
    if (val) {
      sessionStorage.setItem('licenseFilterFromDate', val.toISOString());
    } else {
      sessionStorage.removeItem('licenseFilterFromDate');
    }
    resetToFirstPage();
  };

  const handleSetToDate = (val: moment.Moment | null) => {
    setToDate(val);
    if (val) {
      sessionStorage.setItem('licenseFilterToDate', val.toISOString());
    } else {
      sessionStorage.removeItem('licenseFilterToDate');
    }
    resetToFirstPage();
  };

  return (
    <MainContentPanel>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="h5">
          Kunder
          <IconButton aria-label="Legg til kunde" onClick={(event) => setMenuAnchor(event.currentTarget)} size="large">
            <Add />
          </IconButton>
        </Typography>

        <Typography variant="h5">Serie:</Typography>
        <Tooltip placement="top" title="Velg lisenser med serie: Alle, BFS med delserier eller BVN!">
          <Select value={series} onChange={handleChange}>
            <MenuItem value={0}>Alle</MenuItem>
            <MenuItem value={9}>BFS</MenuItem>
            <MenuItem value={16}>BVN</MenuItem>
          </Select>
        </Tooltip>
      </Stack>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => handleMenuClick(setOpenDialog)}>Fyll ut manuelt</MenuItem>
        <MenuItem onClick={() => handleMenuClick(setOpenSearch)}>Hent fra kundekort i Maconomy</MenuItem>
      </Menu>
      {error && <Typography>{error.message}</Typography>}
      {loading && !data && <LoadingSpinner />}
      {(data || !loading) && (
        <SearchableLicenseTable
          setColumns={handler}
          licenses={licenses}
          loading={loading}
          exportLoading={exportLoading}
          loadExportLicenses={loadExportLicenses}
          rowCount={rowCount}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          onlyActive={onlyActive}
          onlyLocked={onlyLocked}
          onlyPendTerm={onlyPendTerm}
          onlyADFS={onlyADFS}
          onlyInformed={onlyInformed}
          enableDateFilter={enableDateFilter}
          fromDate={fromDate}
          toDate={toDate}
          setSearchTermParent={handleSetSearchTermParent}
          setOnlyActive={handleSetOnlyActive}
          setOnlyLocked={handleSetOnlyLocked}
          setOnlyPendTerm={handleSetOnlyPendTerm}
          setOnlyADFS={handleSetOnlyADFS}
          setOnlyInformed={handleSetOnlyInformed}
          setEnableDateFilter={handleSetEnableDateFilter}
          setFromDate={handleSetFromDate}
          setToDate={handleSetToDate}
          columnlist={columnlist}
          searchTermParent={searchTermParent}
        />
      )}

      <SearchMaconomyCustomerDialog open={openSearch} onCancel={() => setOpenSearch(false)} onSubmit={handleCustomer} />
      <AddLicenseDialog open={Boolean(customer)} customer={customer} onClose={() => setCustomer(undefined)} />
      <AddLicenseDialog open={openDialog} onClose={() => setOpenDialog(false)} />
    </MainContentPanel>
  );
}
